import { OperationTask } from './../components/operation-task/store/operation-task.model';
import { Injectable } from '@angular/core';
import {
  Firestore,
  collection,
  doc,
  getDoc,
  Timestamp,
  query,
  where,
  collectionSnapshots,
} from '@angular/fire/firestore';
import { Observable, of, from, EMPTY } from 'rxjs';
import { switchMap, mergeMap, map, catchError } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { UserState } from '../components/user/store/user.reducer';
import * as fromUserSelector from '../components/user/store/user.selectors';
import {
  endOfMonth,
  endOfWeek,
  format,
  fromUnixTime,
  startOfMinute,
  startOfWeek,
} from 'date-fns';
import { Apollo, gql } from 'apollo-angular';

@Injectable({
  providedIn: 'any',
})
export class ContactTasksService {
  CREATE_CONTACT_TASK = gql`
    mutation CreateContactTask($task: TaskInput!) {
      createContactTask(task: $task)
    }
  `;
  UPDATE_CONTACT_TASK = gql`
    mutation updateContactTask($task: TaskUpdateInput!) {
      updateContactTask(task: $task)
    }
  `;

  DELETE_CONTACT_TASK = gql`
    mutation DeleteContactTask($entiteId: ID!, $id: ID!) {
      deleteContactTask(entiteId: $entiteId, id: $id)
    }
  `;
  DELETE_CONTACT_TAKS = gql`
    mutation DeletContactTasks($id: ID!) {
      deleteContactTasks(id: $id)
    }
  `;

  constructor(
    private db: Firestore,
    private userStore: Store<UserState>,
    private apollo: Apollo
  ) {}

  getAll(itemId: string): Observable<any> {
    try {
      return this.userStore.select(fromUserSelector.user).pipe(
        switchMap((user) => {
          if (!user || !itemId) {
            return of([]);
          } else {
            const dbCollection = collection(
              this.db,
              `organisations/${user.organisation.id}/contacts/${itemId}/tasks`
            );

            const changes = collectionSnapshots(dbCollection).pipe(
              map((changes) => {
                return changes.map((snap) => {
                  const data = snap.data();
                  const id = snap.id;
                  return { id, ...data };
                });
              })
            );

            return changes;
          }
        })
      );
    } catch (error) {
      return of(error);
    }
  }

  getAllUserTasks(itemId: string): Observable<any> {
    try {
      return this.userStore.select(fromUserSelector.user).pipe(
        switchMap((user) => {
          if (!user || !itemId) {
            return of([]);
          } else {
            const dbCollection = collection(
              this.db,
              `organisations/${user.organisation.id}/contacts/${itemId}/tasks`
            );

            const queries = query(
              dbCollection,
              where(`ids`, 'array-contains', user.id)
            );

            const changes = collectionSnapshots(queries).pipe(
              map((changes) => {
                return changes.map((snap) => {
                  const data = snap.data();
                  const id = snap.id;
                  return { id, ...data };
                });
              })
            );

            return changes;
          }
        })
      );
    } catch (error) {
      return of(error);
    }
  }

  getAllTodayTasks(itemId: string): Observable<any> {
    try {
      return this.userStore.select(fromUserSelector.user).pipe(
        switchMap((user) => {
          if (!user || !itemId) {
            return of([]);
          } else {
            const dbCollection = collection(
              this.db,
              `organisations/${user.organisation.id}/contacts/${itemId}/tasks`
            );

            const today = new Date(Date.now());
            const todayDate = Timestamp.fromDate(today);

            const queries = query(
              dbCollection,
              where(`dateStart`, '==', todayDate)
            );

            const changes = collectionSnapshots(queries).pipe(
              map((changes) => {
                return changes.map((snap) => {
                  const data = snap.data();
                  const id = snap.id;
                  return { id, ...data };
                });
              })
            );

            return changes;
          }
        })
      );
    } catch (error) {
      return of(error);
    }
  }
  getAllWeekTasks(itemId: string): Observable<any> {
    try {
      return this.userStore.select(fromUserSelector.user).pipe(
        switchMap((user) => {
          if (!user || !itemId) {
            return of([]);
          } else {
            const dbCollection = collection(
              this.db,
              `organisations/${user.organisation.id}/contacts/${itemId}/tasks`
            );

            const today = new Date(Date.now());
            const startDate: Date = startOfWeek(today);
            const endDate: Date = endOfWeek(today);
            const startTime = Timestamp.fromDate(startDate);
            const endTime = Timestamp.fromDate(endDate);

            const queries = query(
              dbCollection,
              where(`dateStart`, '>=', startTime),
              where(`dateStart`, '<=', endTime)
            );

            const changes = collectionSnapshots(queries).pipe(
              map((changes) => {
                return changes.map((snap) => {
                  const data = snap.data();
                  const id = snap.id;
                  return { id, ...data };
                });
              })
            );

            return changes;
          }
        })
      );
    } catch (error) {
      return of(error);
    }
  }
  getAllMonthTasks(itemId: string): Observable<any> {
    try {
      return this.userStore.select(fromUserSelector.user).pipe(
        switchMap((user) => {
          if (!user || !itemId) {
            return of([]);
          } else {
            const dbCollection = collection(
              this.db,
              `organisations/${user.organisation.id}/contacts/${itemId}/tasks`
            );

            const today = new Date(Date.now());
            const startDate: Date = startOfMinute(today);
            const endDate: Date = endOfMonth(today);
            const startTime = Timestamp.fromDate(startDate);
            const endTime = Timestamp.fromDate(endDate);

            const queries = query(
              dbCollection,
              where(`dateStart`, '>=', startTime),
              where(`dateStart`, '<=', endTime)
            );

            const changes = collectionSnapshots(queries).pipe(
              map((changes) => {
                return changes.map((snap) => {
                  const data = snap.data();
                  const id = snap.id;
                  return { id, ...data };
                });
              })
            );

            return changes;
          }
        })
      );
    } catch (error) {
      return of(error);
    }
  }

  getOne(itemId: string, id: string): Observable<any> {
    try {
      return this.userStore.select(fromUserSelector.user).pipe(
        switchMap((user) => {
          if (!user || !itemId) {
            return of(null);
          } else {
            const dbCollection = collection(
              this.db,
              `organisations/${user.organisation.id}/contacts/${itemId}/tasks`
            );

            const documentRef = doc(dbCollection, id);
            return from(
              getDoc(documentRef)
                .then((snap) => {
                  const data = snap.data();
                  const id = snap.id;
                  return { id, ...data };
                })
                .catch((error) => error)
            ).pipe(mergeMap((res: any) => of(res)));
          }
        })
      );
    } catch (error) {
      return of(error);
    }
  }

  addOne(entiteId: string, task: OperationTask): Observable<any> {
    try {
      if (!task || !entiteId) return EMPTY;

      const { title, description, texts, priority, dateStart, dateEnd } = task;

      const newTask = {
        title,
        description,
        texts,
        priority,
        dateStart,
        dateEnd,
        item: { id: entiteId },
      };
      return this.apollo.mutate({
        mutation: this.CREATE_CONTACT_TASK,
        variables: {
          task: newTask,
        },
      });
    } catch (error) {
      return of(error);
    }
  }

  updateOne(task: any, type?: string): Observable<any> {
    try {
      if (!task) return EMPTY;
      return this.apollo.mutate({
        mutation: this.UPDATE_CONTACT_TASK,
        variables: {
          task: task,
        },
      });
    } catch (error) {
      return of(error);
    }
  }

  deleteOne(entiteId: string, id: string): Observable<any> {
    try {
      if (!entiteId || !id) return EMPTY;
      return this.apollo.mutate({
        mutation: this.DELETE_CONTACT_TASK,
        variables: {
          entiteId: entiteId,
          id: id,
        },
      });
    } catch (error) {
      return of(error);
    }
  }

  deleteAll(id: string): Observable<any> {
    try {
      if (!id) return EMPTY;
      return this.apollo.mutate({
        mutation: this.DELETE_CONTACT_TAKS,
        variables: {
          id: id,
        },
      });
    } catch (error) {
      return of(error);
    }
  }
}
